import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import gtssLogo from '../../../assests/images/gtss_logo.jpeg';
import queryClient from '../../../lib/queryClient';
import { isAdmin } from '../../../lib/roleUtils';
import './sidebar.css';

// ─── Menu Configuration ───────────────────────────────────────────────────────
// Add / remove menu groups and items here without touching JSX
const MENU_CONFIG = [
  {
    id: 'business',
    label: 'Business',
    icon: '💼',
    children: [
      { label: 'Data Management', to: '/business/data-management' },
      { label: 'Agent Management', to: '/business/agent' },
      { label: 'New Packing List', to: '/business/packing-list' },
    ],
  },
  {
    id: 'master-data',
    label: 'Master Data',
    icon: '📋',
    children: [
      { label: 'Add Importer', to: '/customer/add' },
      { label: 'View Importer', to: '/customer/view' },
      { label: 'Add Exporter', to: '/supplier/add' },
      { label: 'View Exporter', to: '/supplier/view' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: '🔧',
    children: [
      { label: 'Add User', to: '/admin/add-user' },
      { label: 'User Management', to: '/admin/users' },
    ],
  },
];


// ─── Role badge config ─────────────────────────────────────────────────────────
const ROLE_BADGE = {
  'super admin':   { label: 'Super Admin',  bg: '#1a56db', color: '#fff' },
  'admin':         { label: 'Admin',         bg: '#0891b2', color: '#fff' },
  'limited admin': { label: 'Shipper',       bg: '#7c3aed', color: '#fff' },
  'importer':      { label: 'Importer',      bg: '#f97316', color: '#fff' },
};

// ─── Profile Card ─────────────────────────────────────────────────────────────
const ProfileCard = () => {
  const isImporter = sessionStorage.getItem('importer') === 'true';
  const userName   = sessionStorage.getItem('userName')  || (isImporter ? 'Importer' : 'User');
  const userEmail  = sessionStorage.getItem('userEmail') || '';
  const rawRole    = sessionStorage.getItem('userRole')  || '';
  const roleKey    = isImporter ? 'importer' : rawRole.trim().toLowerCase();
  const badge      = ROLE_BADGE[roleKey] || { label: rawRole || 'User', bg: '#64748b', color: '#fff' };

  // Avatar: first letter of each word (max 2)
  const initials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '10px',
        padding: '10px 11px',
        marginBottom: '12px',
      }}
    >
      {/* Avatar */}
      <div
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 36, height: 36,
          borderRadius: '50%',
          background: badge.bg,
          color: badge.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
        }}
      >
        {initials || '?'}
      </div>

      {/* Name + role */}
      <div style={{ overflow: 'hidden', flex: 1 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 700,
          color: 'var(--sb-text, #1e293b)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {userName}
        </div>
        {userEmail && (
          <div style={{
            fontSize: 10.5, color: '#64748b',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {userEmail}
          </div>
        )}
        {/* Role badge */}
        <span style={{
          display: 'inline-block',
          marginTop: 3,
          padding: '1px 7px',
          borderRadius: 20,
          fontSize: 9.5, fontWeight: 700,
          letterSpacing: 0.4,
          background: badge.bg,
          color: badge.color,
          textTransform: 'uppercase',
        }}>
          {badge.label}
        </span>
      </div>
    </div>
  );
};

// ─── Sidebar Content (shared between desktop & offcanvas) ─────────────────────
const SidebarContent = ({ onLogout }) => {
  const { pathname } = useLocation();

  // Check role & session
  const isImporter = sessionStorage.getItem('importer') === 'true';
  const userRole = sessionStorage.getItem('userRole') ?? '';
  const isLimitedAdmin = userRole.trim().toLowerCase() === 'limited admin';
  const isUserAdmin = isAdmin(userRole);

  // Determine visible menu per role
  let visibleMenu = MENU_CONFIG;
  if (isUserAdmin) {
    // Admin role receives PKL Management navigating to /business/agent
    visibleMenu = [
      {
        id: 'business',
        label: 'Business',
        icon: '💼',
        children: [
          { label: 'PKL Management', to: '/business/agent' },
        ],
      },
    ];
  } else if (isImporter || isLimitedAdmin) {
    visibleMenu = [
      {
        id: 'business',
        label: 'Business',
        icon: '💼',
        children: [
          { label: 'Data Management', to: '/business/data-management' },
        ],
      },
    ];
  }

  const [activeTheme, setActiveTheme] = useState(() => {
    return localStorage.getItem('gtss-theme') || 'theme-default';
  });

  useEffect(() => {
    document.documentElement.className = activeTheme;
    localStorage.setItem('gtss-theme', activeTheme);
  }, [activeTheme]);

  const handleThemeChange = (newTheme) => {
    setActiveTheme(newTheme);
    window.dispatchEvent(new CustomEvent('gtss-theme-changed', { detail: newTheme }));
  };

  useEffect(() => {
    const handleSync = (e) => {
      setActiveTheme(e.detail);
    };
    window.addEventListener('gtss-theme-changed', handleSync);
    return () => window.removeEventListener('gtss-theme-changed', handleSync);
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();   // clears isLoggedIn, userType, importer, userRole, etc.
    queryClient.clear();      // clears all React Query cache — prevents stale data on next login
    if (onLogout) onLogout();
  };

  return (
    <div className="sb-inner d-flex flex-column h-100">

      {/* ── Logo Section ── */}
      <div className="sb-logo-section">
        <NavLink to="/" aria-label="Go to dashboard">
          <img
            src={gtssLogo}
            alt="Global Trade and Sourcing Solutions"
            className="sb-logo"
          />
        </NavLink>
      </div>

      {/* ── Navigation ── */}
      <nav className="sb-nav flex-grow-1" aria-label="Main navigation">


        {/* Dynamic accordion menu groups */}
        <div className="accordion accordion-flush" id="sidebarAccordion">
          {visibleMenu?.map((group) => {
            const isGroupActive = group.children.some((item) => item.to === pathname);

            return (
              <div className="sb-accordion-item" key={group.id}>

                {/* Parent — accordion toggle button */}
                <button
                  id={`btn-${group.id}`}
                  className={`sb-parent-btn ${isGroupActive ? 'sb-parent-btn--active' : 'collapsed'}`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse-${group.id}`}
                  aria-expanded={isGroupActive ? 'true' : 'false'}
                  aria-controls={`collapse-${group.id}`}
                >
                  <span className="sb-parent-label">
                    <span className="sb-link-icon" aria-hidden="true">{group.icon}</span>
                    {group.label}
                  </span>
                  <span className="sb-chevron" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                    </svg>
                  </span>
                </button>

                {/* Children — collapsible list */}
                <div
                  id={`collapse-${group.id}`}
                  className={`collapse ${isGroupActive ? 'show' : ''}`}
                  data-bs-parent="#sidebarAccordion"
                >
                  <ul className="sb-child-list list-unstyled mb-0">
                    {group.children.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          className={({ isActive }) =>
                            `sb-child-link${isActive ? ' sb-link--active' : ''}`
                          }
                        >
                          {item.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            );
          })}
        </div>
      </nav>

      {/* ── Profile Card ── */}
      <div className="sb-footer">

        {/* Profile Info */}
        <ProfileCard />

        {/* Theme Selector */}
        <div className="mb-3 px-1">
          <label htmlFor="theme-select" className="form-label mb-1 text-uppercase fw-bold text-secondary" style={{ fontSize: '10px', letterSpacing: '0.6px' }}>
            🎨 Interface Color
          </label>
          <select
            id="theme-select"
            className="form-select form-select-sm border-secondary-subtle"
            value={activeTheme}
            onChange={(e) => handleThemeChange(e.target.value)}
            style={{ fontSize: '12px', fontWeight: '500', height: '34px', cursor: 'pointer' }}
          >
            <option value="theme-default">Modern Blue &amp; Orange</option>
            <option value="theme-teal">Ocean Teal &amp; Slate</option>
            <option value="theme-classic">Classic Navy &amp; Amber</option>
            <option value="theme-indigo">Indigo &amp; Mint</option>
            <option value="theme-steel">Steel Blue &amp; Cool Grey</option>
            <option value="theme-logo-corp">Logo Corporate (Navy &amp; Orange)</option>
            <option value="theme-logo-classic">Logo Classic (Navy &amp; Amber)</option>
            <option value="theme-logo-sunset">Logo Sunset (Charcoal &amp; Sun Orange)</option>
          </select>
        </div>

        <button
          type="button"
          id="logout-btn"
          className="sb-logout-btn"
          aria-label="Log out of the application"
          onClick={handleLogout}
        >
          <span aria-hidden="true">🚪</span> Logout
        </button>
      </div>

    </div>
  );
};

// ─── Sidebar Component ────────────────────────────────────────────────────────
/**
 * Sidebar — Global Trade & Sourcing Solutions (GTSS)
 *
 * Responsive behavior:
 *   Desktop (≥ 992px) → Fixed sidebar, 260px wide
 *   Tablet  (768–991px) → Hidden; hamburger toggle shows offcanvas
 *   Mobile  (< 768px)  → Hidden; hamburger toggle shows offcanvas
 *
 * Uses Bootstrap 5 Offcanvas + Accordion — no custom JS.
 */
const Sidebar = ({ onLogout }) => (
  <>
    {/* ══════════════════════════════════════════════════
        DESKTOP SIDEBAR — visible on lg and above
        ══════════════════════════════════════════════════ */}
    <aside
      id="desktop-sidebar"
      className="sb-desktop d-none d-lg-flex flex-column"
      aria-label="Desktop sidebar navigation"
    >
      <SidebarContent onLogout={onLogout} />
    </aside>

    {/* ══════════════════════════════════════════════════
        MOBILE / TABLET — hamburger button (shown < lg)
        ══════════════════════════════════════════════════ */}
    <button
      id="sidebar-hamburger"
      className="sb-hamburger d-lg-none"
      type="button"
      data-bs-toggle="offcanvas"
      data-bs-target="#mobileSidebar"
      aria-controls="mobileSidebar"
      aria-label="Open navigation menu"
    >
      <span className="navbar-toggler-icon" />
    </button>

    {/* ══════════════════════════════════════════════════
        OFFCANVAS SIDEBAR — mobile & tablet (< lg)
        ══════════════════════════════════════════════════ */}
    <div
      className="offcanvas offcanvas-start sb-offcanvas"
      tabIndex="-1"
      id="mobileSidebar"
      aria-labelledby="mobileSidebarLabel"
    >
      <div className="offcanvas-header sb-offcanvas-header">
        <span id="mobileSidebarLabel" className="visually-hidden">Navigation menu</span>
        <button
          type="button"
          className="btn-close btn-close-white ms-auto"
          data-bs-dismiss="offcanvas"
          aria-label="Close navigation menu"
        />
      </div>
      <div className="offcanvas-body p-0">
        <SidebarContent onLogout={onLogout} />
      </div>
    </div>
  </>
);

export default Sidebar;